<template>
  <div
    class="upcoming-events-carousel__card"
    data-category="event-card"
    :data-type="event.eventTypes.name"
  >
    <div
      class="upcoming-events-carousel__card-wrapper"
    >
      <picture
        v-lazyload="{
          src: event.eventImage,
          variant: 'default',
          updateCallback: imageLoadedEvent,
          canLoad,
        }"
        class="upcoming-events-carousel__image image-wrapper"
      >
        <img
          class="image"
          src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
          :alt="event.eventImageAlt"
        >
      </picture>
      <div class="upcoming-events-carousel__container">
        <div class="upcoming-events-carousel__info">
          <span
            v-if="event.eventTypes && event.eventTypes.name"
            class="upcoming-events-carousel__tag"
          >
            {{ event.eventTypes.name }}
          </span>
          <span class="upcoming-events-carousel__eyebrown">
            {{ eventEyebrownLabel }}
          </span>
          <h3 class="upcoming-events-carousel__title">
            {{ event.eventTitle }}
          </h3>
          <div
            :id="`desc-${event.eventId}`"
            class="upcoming-events-carousel__description"
            v-html="truncatedDescription"
          />
          <button
            v-if="descriptionShouldBeTruncated"
            class="upcoming-events-carousel__description-cta"
            type="button"
            data-category="toggle-description"
            :data-state="showFullDescription ? 'expanded' : 'collapsed'"
            :class="{'more': !showFullDescription, 'less': showFullDescription}"
            :aria-expanded="showFullDescription"
            :aria-controls="`desc-${event.eventId}`"
            @click="toggleDescriptionVisibility"
            @keydown.enter.prevent="toggleDescriptionVisibility"
            @keydown.space.prevent="toggleDescriptionVisibility"
          >
            {{ showFullDescription ?
              dictionary['upcoming-events:see-less'] :
              dictionary['upcoming-events:see-more']
            }}
          </button>
          <h4
            v-if="event.additionalDates.length"
            class="upcoming-events-carousel__date-title"
          >
            {{ dictionary['upcoming-events:additional-dates'] }}
          </h4>
          <ul
            v-if="event.additionalDates.length"
            class="upcoming-events-carousel__date-list"
          >
            <li
              v-for="(date, index) in event.additionalDates"
              :key="`event-date-${event.eventId}-${index}`"
              class="upcoming-events-carousel__date-item"
            >
              <span>{{ eventDayOfWeek(date.eventStartDate) }}</span>
              <span>{{ eventMonthDay(date.eventStartDate) }}</span>
              <span class="upcoming-events-carousel__date-item-time">
                {{ date.eventStartTime }} {{ date.eventEndTime ? `- ${date.eventEndTime}` : '' }}
              </span>
            </li>
          </ul>
        </div>
        <div class="upcoming-events-carousel__ctas">
          <a
            v-if="event.secondaryCtaUrl && event.secondaryCtaLabel"
            class="button--secondary black size-md outlined"
            :href="event.secondaryCtaUrl"
            :target="event.secondaryCtaTarget"
            data-category="cta"
            :data-cta="toCamelCase(event.secondaryCtaLabel)"
          >{{ event.secondaryCtaLabel }}</a>
          <a
            v-if="event.primaryCtaUrl && event.primaryCtaLabel"
            class="button--primary black size-md"
            :href="event.primaryCtaUrl"
            :target="event.primaryCtaTarget"
            data-category="cta"
            :data-cta="toCamelCase(event.primaryCtaLabel)"
          >{{ event.primaryCtaLabel }}</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import truncate from 'html-truncate'
  import { formatDate } from '@utils/date-helpers'
  import { toCamelCase } from '@utils/string-helpers'

  export default {
    name: 'UpcomingEventsSlide',
    inject: ['dictionary'],
    props: {
      event: {
        type: Object,
        required: true,
      },
      imageLoadedEvent: {
        type: Function,
        required: true,
      },
      canLoad: {
        type: Boolean,
        required: true,
      },
    },
    data() {
      return {
        showFullDescription: false,
      }
    },
    computed: {
      ...mapState('responsive', { wWidth: 'width' }),
      ...mapGetters('i18n', ['tc']),
      descriptionMaxLength() {
        return this.wWidth > 768 ? 190 : 80
      },
      descriptionShouldBeTruncated() {
        return this.event?.eventDescription?.length > this.descriptionMaxLength
      },
      truncatedDescription() {
        if (!this.showFullDescription
          && this.descriptionShouldBeTruncated) {
          return truncate(this.event.eventDescription, this.descriptionMaxLength)
        }
        return this.event.eventDescription
      },
      eventEyebrownLabel() {
        return `${this.eventDayOfWeek(this.event.eventStartDate)}, ${this.eventMonthDay(this.event.eventStartDate)} | ${this.event.eventStartTime}`
      }
    },
    methods: {
      toCamelCase,
      toggleDescriptionVisibility() {
        this.showFullDescription = !this.showFullDescription
      },
      eventDayOfWeek(date) {
        return formatDate(new Date(date), 'MM/DD/YYYY', 'dddd')
      },
      eventMonthDay(date) {
        return formatDate(new Date(date), 'MM/DD/YYYY', 'MMMM D')
      },
    }
  }
</script>
